import type { Endpoint, Request, RequestArgs, Route } from '../types.no-deps'
import type {
    ActionWithAssignees,
    Frequency,
    OncePer,
    ScheduleWithAssignees,
} from '../../../shared/domain/schedule/types.no-deps'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import type { FakeTopLevelJsonLogicForJoi } from '../../../shared/webcomponents/zaptic-dynamic-conditions.no-deps'
import type { AddableUnitWorkflowParameter } from '../../../shared/domain/UnitWorkflowParameter/types.no-deps'

export const route: Route = { method: 'POST', pattern: '/api/schedules' }

export type AddableSchedule = OneOffAddableSchedule | RecurringAddableSchedule | EventAddableSchedule

export type BackwardsCompatibleAddableSchedule = AddableSchedule & {
    subjectUnitId?: number
    parameters?: AddableUnitWorkflowParameter[]
    extraMetaData?: ExtraMetaData
} // for `subjectUnitId` & parameters backwards compatibility

export interface OneOffAddableSchedule extends AddableScheduleBase {
    dueDate: Date
}

export interface RecurringAddableSchedule extends AddableScheduleBase {
    occurrences: number
    frequency: Frequency
    endDate?: Nullable<Date>
    interval?: number
    dueDate?: Date
    durationUnit?: Nullable<Frequency>
    durationValue?: Nullable<number>
}

export interface EventAddableSchedule extends AddableScheduleBase {
    frequency: 'onevent'
    onWhichEventKey: string
    interval: number
    durationUnit: Frequency
    durationValue: number
}
// TODO type this in a way that represents the fact that an action has a description and an origin
interface AddableScheduleBase {
    startDate: Date
    oncePer: OncePer
    timezone: string // london/europe
    showOverdue?: boolean // yes
    procedure?: Nullable<number> // no
    workflow?: Nullable<number> // no
    subjectUnitIds?: Nullable<number[]>
    // TODO keep the following 3 fields optional until the portal starts sending this
    allTeams?: Nullable<boolean> // no
    description?: Nullable<string> // action name
    origin?: Nullable<number> // report id
    isAction?: Nullable<boolean>
    teams?: number[] // no
    users: number[] // [user]
    excludedUsers?: number[]
    allProcedureTeams?: Nullable<boolean>
    automaticCollaboration?: boolean
    visibilityCondition?: Nullable<FakeTopLevelJsonLogicForJoi>
    autocompleteOverdue?: boolean
    adjustToDst?: boolean
}

type ExtraMetaData = Array<[string, string]>

type Params = never
type Body = BackwardsCompatibleAddableSchedule
type Query = never
type Output = Array<ScheduleWithAssignees | ActionWithAssignees>

export type AddEndpoint = Endpoint<Params, Body, Query, Output>

export const addRequest = (request: Request, args: RequestArgs<AddEndpoint>) => request<AddEndpoint>(route, args)
